<template>
	<component :is="tag"
			   class="sortable-item"
			   :class="classObject"
			   v-bind="$attrs"
			   v-on="$listeners"
			   :draggable="(handle == null) || this.isDragging"
			   @dragover.prevent
			   @dragstart.stop="dragstart"
			   @dragend="dragend"
			   @dragenter="dragenter"
			   @dragleave="dragleave"
			   @drop.prevent="drop">
		<slot />
	</component>
</template>

<script lang="ts">
import Vue, { CreateElement } from "vue";
import { Component, Prop, Watch, Inject } from "vue-property-decorator";
import Sortable from './Sortable.vue';

@Component
export default class SortableItem extends Vue {
	@Prop() value: any;
	@Prop({ type: String, default: ".sortable-handle" }) handle: string;
	@Prop({ type: String, default: "v-list-item" }) tag: string;
	@Prop({ type: Boolean, default: true }) draggable: boolean;

	isOver: boolean = false;
	isDragging: boolean = false;
	isInsertingBefore: boolean = false;
	clone: Element = null;

	mounted() {
		if (this.handle != null)
			this.setupHandle();
	}

	get classObject() {
		return {
			'active': this.isDragging,
			'over': this.isOver
		};
	}

	setupHandle() {
		const isMatch = (element: Element) => {
			if (element == null)
				return false;
			if (element.matches(this.handle))
				return true;
			if (element != this.$el && element != document.body)
				return isMatch(element.parentElement);
			return false;
		}

		const dragStart: EventListenerOrEventListenerObject = (e) => {
			if (!isMatch(e.target as Element))
				return;
			this.isDragging = this.draggable
		};

		this.$el.addEventListener("mousedown", dragStart);
		this.$on('beforeDestroy', () => {
			this.$el.removeEventListener("mousedown", dragStart);
		});
	}

	@Inject()
	sortable: Sortable;

	dragstart(e: DragEvent) {
		// Safari is too stupid to properly create a 'ghost'
		// element, so here we are...
		const clone = this.cloneFromEvent(e);
		e.dataTransfer.setDragImage(clone, 0, 0);
		this.clone = clone;

		// Firefox requires you to setData on the dataTransfer
		// Use "text" instead of "text/plain" (if applicable)
		e.dataTransfer.setData('text', 'banana');
		this.sortable.setSortable(this.value);

		this.isDragging = true;
	}

	cloneFromEvent(e: DragEvent) {
		const el = e.target as Element;
		const clone = document.createElement("div");
		//const clone = el.cloneNode(true);
		clone.classList.add("sortable-clone");
		const styles = getComputedStyle(el, null);
		clone.style.width = styles.width;
		clone.style.height = styles.height;
		el.parentElement.append(clone);
		return clone;
	}

	dragend(e: DragEvent) {
		this.isDragging = false;
		if (this.clone) {
			this.clone.remove();
			this.clone = null;
		}
		this.sortable.done();
	}

	dragenter(e: DragEvent) {
		if (this.isDragging)
			return;

		if (!this.sortable.isDragging)
			return;

		this.sortable.setOver(this.value);
		this.isOver = true;
	}

	dragleave(e: DragEvent) {
		this.isOver = false;
	}

	drop(e: DragEvent) {
		const data = e.dataTransfer.getData("text");
		this.isOver = false;

		this.sortable.setDroppable(this.value);

		return data === "banana";
	}
}
</script>

<style lang="scss">
	.sortable-item {
		&.active {
			outline: dashed 2px;
			opacity: 0.5;
		}

		&.active,
		&.over {
			> * {
				pointer-events: none;
			}
		}

		&.slide-y-transition-move {
			transition-duration: 0.3s, 0.3s;
			pointer-events: none;
		}
	}

	.sortable-handle {
		cursor: grab;
	}

	.sortable-clone {
		position: absolute;
		left: -100%;
	}
</style>